import Box from "@material-ui/core/Box";
import React, { ReactNode } from "react";
import { v4 as uuid } from "uuid";
import StyledTooltip from "../Wrapper/StyledTooltip";

export interface BlackChipTooltipProps {
  children: React.ReactElement<any>;
  header?: ReactNode;
  subHeader?: string;
  section: string;
  secondaryChips: any[];
  optionalChips?: any[];
  noDefaultTitle?: boolean;
  noChip?: boolean;
}

const BlackChipTooltip: React.FC<BlackChipTooltipProps> = (props) => {
  const { children, header, section, secondaryChips, optionalChips, noChip, noDefaultTitle, subHeader } = props;
  const defaultTitle = noDefaultTitle ? false : <Box component="div">No actions defined</Box>;

  const headerElement = header && <Box fontWeight="bold">{header}</Box>;

  const secondaryChipsTemplate = (secondaryChips: any[], section?: string): JSX.Element | JSX.Element[] => {
    let individualActions: JSX.Element[] = [];
    if (section === "condition") {
      individualActions = secondaryChips.map((secondaryChip: any) => {
        const name = secondaryChip.parameter?.name;
        const { value, unitCode } = secondaryChip;

        return (
          <div key={uuid()}>
            {name}: {value} {unitCode || ""}
          </div>
        );
      });
    } else {
      individualActions = secondaryChips.map((secondaryChip: any) => {
        const name = secondaryChip.action?.name;
        const { parameterValues } = secondaryChip;
        let usedParams = 0;
        const values = parameterValues.map((parameterValue: any) => {
          const { uncontrolled, value, unitCode } = parameterValue;

          let params;
          if (uncontrolled) {
            params = "";
          } else {
            params = `${usedParams > 0 ? ", " : ""}${value} ${unitCode || ""}`;
            usedParams += 1;
          }

          return params;
        });

        return (
          <div key={uuid()}>
            {name}: {values}
          </div>
        );
      });
    }

    return individualActions.length ? (
      [
        <Box key={uuid()} component="span" fontWeight="bold">
          Individual actions:
        </Box>,
        ...individualActions,
      ]
    ) : (
      <Box component="span">No individual actions</Box>
    );
  };

  const optionalChipsTemplate = (optionalChips: any[], section?: string): (any[] | JSX.Element)[] | JSX.Element => {
    let actionsOnAll: (any[] | JSX.Element)[] = [];

    if (section === "condition") {
      actionsOnAll = optionalChips?.map((optionalChip: any) => {
        const name = optionalChip.parameter?.name;
        const { value, unitCode } = optionalChip;

        return (
          <div key={uuid()}>
            {name}: {value} {unitCode || ""}
          </div>
        );
      });
    } else {
      optionalChips.map((optionalChip: any) => {
        let usedParams = 0;
        let paramKeys = "";

        actionsOnAll = optionalChip?.rows?.map((row: any) => {
          const paramValues = row.parameterValues.map((parameterValue: any, i: number) => {
            const { parameter, uncontrolled, value, unitCode } = parameterValue;

            let params;
            if (i < row.parameterValues.length - 1) {
              paramKeys += `${parameter.name}, `;
            } else {
              paramKeys += `${parameter.name}`;
            }

            if (uncontrolled) {
              params = "";
            } else {
              params = `${usedParams > 0 ? ", " : ""}${value} ${unitCode || ""}`;
              usedParams += 1;
            }

            return params;
          });

          return (
            <div key={uuid()}>
              {paramKeys}: {paramValues}
            </div>
          );
        });
      });
    }
    return actionsOnAll.length ? (
      [
        <Box key={uuid()} component="span" fontWeight="bold">
          Actions on all:
        </Box>,
        ...actionsOnAll,
      ]
    ) : (
      <Box component="span">No actions on all</Box>
    );
  };

  let title;
  switch (section) {
    case "condition":
      title = (
        <>
          <div>{secondaryChipsTemplate(secondaryChips, "condition")}</div>
          <div>{optionalChips && optionalChipsTemplate(optionalChips, "condition")}</div>
        </>
      );
      break;
    case "material":
    case "substrate":
    case "testpiece":
      title = (
        <>
          <div>{secondaryChipsTemplate(secondaryChips)}</div>
          <div>{optionalChips && optionalChipsTemplate(optionalChips)}</div>
        </>
      );
      break;
    default:
      title = <></>;
  }

  if (!(optionalChips && optionalChips.length) && !secondaryChips.length && !subHeader && !headerElement) {
    return children;
  }

  return (
    <StyledTooltip
      title={
        <>
          {headerElement}
          {subHeader}
          {secondaryChips.length || (optionalChips && optionalChips.length) ? title : defaultTitle}
        </>
      }
    >
      {noChip ? children : <Box component="span">{children}</Box>}
    </StyledTooltip>
  );
};

export default BlackChipTooltip;
